import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbDate, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import moment from "moment";
import { combineLatest, merge, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { CheckboxComponent } from "~app/common/components/checkbox/checkbox.component";
import { IconButtonComponent } from "~app/common/components/icon-button/icon-button.component";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import {
    ConfirmDialogResponse,
    Malklass,
    Planeringsstatus,
    SkogsvardAtgardsGrupp,
    SkogsvardAtgardsMetod,
    SkogsvardAtgardsTyp
} from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import {
    EntityType,
    RuleErrorContext,
    RuleSeverityLevel,
    RuleValidationMessage,
    RuleVisibilityStrategy
} from "~app/common/services/cqs/models/rule-validation-message";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { canEdit } from "~app/role-permissions";
import { UpdateOnskatDatumCommand } from "../../commands/skogsvard-atgard/UpdateOnskatDatumCommand";
import { UpdateSkogsvardTradskiktCommand } from "../../commands/skogsvard-tradskikt/UpdateSkogsvardTradskiktCommand";
import { Atgard } from "../../models/atgard-models";
import { AtgardSkogsvardTradslag, SKOGSVARD_PLANERINGSSTATUS } from "../../models/skogsvard/skogsvard-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { AtgardKedjaMediatorService } from "../../services/atgardkedja-mediator.service";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { KontraktCommandService } from "../../services/kontrakt-command.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { SkogsvardForyngringService } from "../../services/skogsvard-foryngring.service";
import { SkogsvardService } from "../../services/skogsvard.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VirkeskopValidationMessageComponent } from "../../validation/validation-message/virkeskop-validation-message.component";
import { SkogsvardDetaljerForComponent } from "./skogsvard-detaljer-for/skogsvard-detaljer-for.component";
import { SkogsvardDetaljerMbComponent } from "./skogsvard-detaljer-mb/skogsvard-detaljer-mb.component";
import { SkogsvardDetaljerRojningComponent } from "./skogsvard-detaljer-rojning/skogsvard-detaljer-rojning.component";
@Component({
    selector: "rask-skogsvard-detaljer",
    templateUrl: "./skogsvard-detaljer.component.html",
    styleUrls: ["./skogsvard-detaljer.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        VirkeskopValidationMessageComponent,
        SimpleSelectorComponent,
        TextmultilineComponent,
        ButtonComponent,
        FormsModule,
        DatepickerComponent,
        SkogsvardDetaljerMbComponent,
        SkogsvardDetaljerForComponent,
        SkogsvardDetaljerRojningComponent,
        InputComponent,
        TextareaComponent,
        NgbTooltip,
        CheckboxComponent,
        IconButtonComponent
    ]
})
export class SkogsvardDetaljerComponent implements OnInit, OnDestroy, AfterViewInit {
    protected atgardPlaneringsstatusLista: any[];
    protected externBeteckningInfotext = "Referens hos kund/leverantör.Tänk på att Extern beteckning + Benämning Kontrakt ej bör överstiga 50 tecken totalt för att visas korrekt i SVP";
    protected kontraktReadonly = false;
    protected malKlasser = Malklass.allaValbara;
    protected skogsvardMetoder = [];
    protected skogsvardTyper = [];
    protected skogsvardsAtgard: Atgard;
    protected isDirty = false;
    protected minSelectableDate = new Date(new Date().setDate(2));

    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly kartaMediatorService = inject(VkKartaMediatorService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly skogsvardForyngringService = inject(SkogsvardForyngringService);
    private readonly skogsvardService = inject(SkogsvardService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly modalService = inject(RaskModalService);
    private readonly busyService = inject(BusyService);
    private readonly toastService = inject(ToastService);
    private readonly canEdit = canEdit();
    private readonly authenticationService = inject(AuthenticationService);

    private atgarder: Atgard[] = [];
    private foryngringTradslag: AtgardSkogsvardTradslag[] = [];
    private initialSkogsvardsTyp: string;
    private isLoading = true;

    constructor() {
        this.skogsvardsAtgard = new Atgard();

        const isDisabled = (status: string) =>
            status === Planeringsstatus.Tillgangligt
            && !this.canEdit();

        this.atgardPlaneringsstatusLista = SKOGSVARD_PLANERINGSSTATUS.map((value) => ({
            text: value,
            value,
            disabled: isDisabled(value)
        }));
    }

    private get kontrakt() {
        return this.kontraktMediatorService.kontrakt;
    }

    private get kontraktGeometrier() {
        return this.kartaMediatorService.kontraktGeometrier$.value;
    }

    ngAfterViewInit(): void {
        window.requestAnimationFrame(() => {
            this.isLoading = false;
        });
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(atg => {
                this.skogsvardsAtgard = atg;
                this.initialSkogsvardsTyp = atg.atgardstyp;
                this.getSkogsvardListItems();
                this.validate(false);
            });

        this.kontraktMediatorService.atgarder$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(a => {
                this.atgarder = a;
            });

        this.skogsvardForyngringService.skogsvardForyngringTradslag$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(f => {
                this.foryngringTradslag = f;
                this.setMalPlantorHa(this.skogsvardsAtgard.atgardstyp);
            });

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.ngDestroyed$))
            .subscribe(readonly => this.kontraktReadonly = readonly);

        this.avtalMediatorService.dirty$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(dirty => {
                this.isDirty = dirty;
            });

        merge(
            this.kartaMediatorService.atgardGeometriChanged$,
            this.kartaMediatorService.atgardGeometriDeleted$,
            this.kartaMediatorService.atgardGeometrier$,
            this.kartaMediatorService.atgardDrivningspunktChanged$,
            this.kartaMediatorService.kontraktGeometriChanged$,
            this.kartaMediatorService.kontraktGeometriDeleted$
        ).pipe(takeUntil(this.ngDestroyed$))
            .subscribe(() => this.validate(false));

        this.validate(true);

        const validator = () => this.validate(false);
        this.avtalMediatorService.registerOnSaveCallback(validator);
    }

    onPlaneringsstatusChange(value: string) {
        this.skogsvardsAtgard.status = value;
        this.atgardMediatorService.update(this.skogsvardsAtgard);
        this.validate(false);
    }

    protected statusDisabled(): boolean {
        return this.kontraktReadonly || ObjectUtils.hasValue(this.skogsvardsAtgard.slutjusteradDatum);
    }

    protected slutjusteraDisabled(): boolean {
        return !this.skogsvardsAtgard.isBetalningJusterbar || this.isDirty || !this.skogsvardsAtgard.isTillgangligtOrSenare || this.kontraktReadonly;
    }

    protected  lasUppvisible(): boolean {
        return this.authenticationService.hasRoles("RASK.Administrera");
    }

    protected canRemoveAtgard(): boolean {
        return !this.kontraktReadonly &&
            !this.skogsvardsAtgard.isTillgangligt &&
            (!this.skogsvardsAtgard.skogsvardStatus || this.skogsvardsAtgard.isSkogsvardStatusInnanTilldelad);
    }

    protected getOnskatdatum(): string {
        if (!_.isNil(this.skogsvardsAtgard.onskatDatum)) {
            return moment(this.skogsvardsAtgard.onskatDatum).format(DateUtils.dateMonthformat);
        }
        return null;
    }

    protected getSlutjusteraddatum(): string {
        if (!_.isNil(this.skogsvardsAtgard.slutjusteradDatum)) {
            return moment(this.skogsvardsAtgard.slutjusteradDatum).format(DateUtils.datetimeformatWithoutSeconds);
        }
        return null;
    }

    protected getSkogsvardOnskatdatum(): string {
        if (!_.isNil(this.skogsvardsAtgard.skogsvardOnskatDatum)) {
            return moment(this.skogsvardsAtgard.skogsvardOnskatDatum).format(DateUtils.dateMonthformat);
        }
        return null;
    }

    protected getSkogsvardStatusHandelsedatum(): string {
        if (!_.isNil(this.skogsvardsAtgard.skogsvardStatusHandelseDatum)) {
            return moment(this.skogsvardsAtgard.skogsvardStatusHandelseDatum).format(DateUtils.datetimeformatWithoutSeconds);
        }
        return null;
    }

    protected isForyngring(): boolean {
        return this.skogsvardsAtgard?.isForyngring ?? false;
    }

    protected isMarkberedning(): boolean {
        return this.skogsvardsAtgard?.isMarkberedning ?? false;
    }

    protected isPlantleverans(): boolean {
        return this.skogsvardsAtgard?.isPlantleverans ?? false;
    }

    protected isRojning(): boolean {
        return this.skogsvardsAtgard?.isRojning ?? false;
    }

    protected onDetaljerChanged(): void {
        this.update();
        this.validate(false);
    }

    protected onOnskatDatumChange(datum: NgbDate): void {
        if (this.skogsvardsAtgard.onskatDatum === null && _.isNil(datum)) {
            return;
        }

        // Konvertera NgbDate objektet från datepickern till ett Date objekt
        this.skogsvardsAtgard.onskatDatum = !_.isNil(datum)
            ? new Date(datum.year, datum.month - 1, datum.day)
            : null;
        const cmd = new UpdateOnskatDatumCommand(this.skogsvardsAtgard.guid, this.skogsvardsAtgard.onskatDatum);
        this.kontraktCommandService.update(cmd);
    }

    protected onRemoveAtgard(): void {
        if (this.kontraktReadonly) {
            return;
        }
        this.atgardMediatorService.removeAtgard();
    }

    protected onSlutjustera(): void {
        const result$ = this.modalService.confirm(new ModalSettings("Slutjustera åtgärd", `Är du säker på att du vill slutjustera åtgärden? Det går inte att göra ändringar på en slutjusterad åtgärd.`));

        result$.subscribe((result) => {
            if (result === ConfirmDialogResponse.cancel) {
                return;
            }

            this.skogsvardsAtgard.slutjusteradDatum = new Date();
            this.sendSlutJusteradCommand(this.skogsvardsAtgard.guid, new Date());
        });
    }

    protected onLasUpp(): void {
        const result$ = this.modalService.confirm(new ModalSettings("Ångra slutjustera", `Slutjusterad åtgärd kan vara hanterad i Vacs. Kontakta virkesekonomi.`));

        result$.subscribe((result) => {
            if (result === ConfirmDialogResponse.cancel) {
                return;
            }

            this.skogsvardsAtgard.slutjusteradDatum = undefined;
            this.sendSlutJusteradCommand(this.skogsvardsAtgard.guid);
        });
    }

    protected onSkogsvardTyperChanged(typ: string): void {
        if (!_.isNil(typ)) {
            this.skogsvardsAtgard.atgardstyp = typ;
            this.getSkogsvardsmetoderForTyp(typ);
            if (this.skogsvardsAtgard.isRojning) {
                this.skogsvardsAtgard.atgardsmetod = SkogsvardAtgardsMetod.motormanuell;
            } else if (this.skogsvardsAtgard.isInventering) {
                this.skogsvardsAtgard.atgardsmetod = SkogsvardAtgardsMetod.objektiv;
            } else {
                this.skogsvardsAtgard.atgardsmetod = null;
            }

            if (this.skogsvardForyngringService.isSadd(this.skogsvardsAtgard)) {
                this.skogsvardsAtgard.malFrogivaHa = this.skogsvardForyngringService.getMalFrogiva(this.foryngringTradslag);
            } else {
                this.handleForyngringTradslag(typ);
            }

            this.handleTradskikt(typ);

            this.update();
            const valid = this.validate(false);

            if (valid) {
                this.initialSkogsvardsTyp = typ;
            }
        }
    }

    protected isSkogsvardsKontrakt(): boolean {
        return this.kontraktMediatorService.kontrakt.isSkogsvardsKontrakt;
    }

    protected isNotSkogsvardFPVKontrakt(): boolean {
        return this.kontraktMediatorService.kontrakt.isNotSkogsvardFPVKontrakt;
    }

    private sendSlutJusteradCommand(atgardGuid: string, tidpunkt?: Date) {
        const slutjusteraAtgard$ = this.atgardMediatorService.slutjusteraAtgard(atgardGuid, this.kontraktMediatorService.kontrakt.id, tidpunkt);

        this.busyService.addWork(slutjusteraAtgard$).subscribe(() => {
            this.toastService.add(`Åtgärd slutjusterad: ${this.skogsvardsAtgard.beteckning}`);
        });
    }

    private changeFromSadd(atgardstyp: string): boolean {
        if (this.isMarkberedningSadd(this.initialSkogsvardsTyp) &&
            !this.isMarkberedningSadd(atgardstyp) &&
            this.foryngringTradslag?.length > 0) {
            return true;
        }

        if (this.isForyngringSadd(this.initialSkogsvardsTyp) &&
            !this.isForyngringSadd(atgardstyp) &&
            this.foryngringTradslag?.length > 0) {
            return true;
        }

        return false;
    }

    private clearValidationMessages(entityType: EntityType) {
        this.validationService.clearMessagesByEntity(
            entityType,
            [
                this.kontraktMediatorService.kontrakt.guid,
                this.skogsvardsAtgard.guid
            ]);
    }

    private createValidationMessage(message: Partial<RuleValidationMessage>): RuleValidationMessage {
        return {
            severity: RuleSeverityLevel.info,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save,
            entityType: "skogsvardsatgard",
            entityGuids: [
                this.kontrakt.guid,
                this.skogsvardsAtgard.guid
            ],
            message: "",
            messageId: "",
            ...message
        };
    }

    private getSkogsvardListItems(): void {
        combineLatest([
            this.skogsvardService.getSkogsvardTyper(this.skogsvardsAtgard?.atgardsgrupp),
            this.skogsvardService.getSkogsvardMetoder(this.skogsvardsAtgard?.atgardstyp)
        ]
        ).pipe(takeUntil(this.ngDestroyed$))
            .subscribe({
                next: ([
                    skogsvardTypLista,
                    skogsvardMetodLista
                ]) => {
                    if (this.isSkogsvardsKontrakt() && this.skogsvardsAtgard?.atgardsgrupp === SkogsvardAtgardsGrupp.rojning) {
                        const allowedSkogsvardsTyper = [SkogsvardAtgardsTyp.hyggesrensning];
                        this.skogsvardTyper = skogsvardTypLista.filter(typ => allowedSkogsvardsTyper.includes(typ));
                    } else {
                        this.skogsvardTyper = skogsvardTypLista;
                    }

                    this.skogsvardMetoder = skogsvardMetodLista;
                },
                error: () => {
                    //Hantera fel
                }
            });
    }

    private getSkogsvardsmetoderForTyp(typ: string): void {
        this.skogsvardService.getSkogsvardMetoder(typ).subscribe(x => {
            this.skogsvardMetoder = x;
        });
    }

    private handleForyngringTradslag(atgardstyp: string): void {
        if (this.changeFromSadd) {
            this.foryngringTradslag.forEach(t => {
                this.skogsvardForyngringService.deleteSkogsvardTradslag(t.guid);
            });
        }
    }

    private handleTradskikt(atgardstyp: string): void {
        if (this.isRojningWithTradskikt(this.initialSkogsvardsTyp) &&
            !this.isRojningWithTradskikt(atgardstyp)) {
            const cmd = new UpdateSkogsvardTradskiktCommand(this.skogsvardsAtgard.guid, []);
            this.kontraktCommandService.update(cmd);
        }
    }

    private isForyngringSadd(atgardstyp: string): boolean {
        return this.skogsvardsAtgard.atgardsgrupp === SkogsvardAtgardsGrupp.foryngring &&
            atgardstyp === SkogsvardAtgardsTyp.foryngringSadd;
    }

    private isMarkberedningSadd(atgardstyp: string): boolean {
        return this.skogsvardsAtgard.atgardsgrupp === SkogsvardAtgardsGrupp.markberedning &&
            atgardstyp === SkogsvardAtgardsTyp.markberedningSadd;
    }

    private isNullOrEmpty(variable: any): boolean {
        return ObjectUtils.isNullOrUndefined(variable) || variable === "";
    }

    private isRojningWithTradskikt(atgardstyp: string): boolean {
        return this.skogsvardsAtgard?.atgardsgrupp === SkogsvardAtgardsGrupp.rojning &&
            atgardstyp === SkogsvardAtgardsTyp.plantrojning ||
            atgardstyp === SkogsvardAtgardsTyp.slutrojning;
    }

    private setMalPlantorHa(atgardstyp: string): void {
        if (this.isMarkberedningSadd(atgardstyp) || this.isForyngringSadd(atgardstyp)) {
            const malFrogiva = this.skogsvardForyngringService.getMalFrogiva(this.foryngringTradslag);
            this.skogsvardsAtgard.malFrogivaHa = malFrogiva === 0 ? null : malFrogiva;
        }
    }

    private update(): void {
        // Kontrollera om vi ändrat åtgärdstypen till eller från siktröjning, isf. sortera om kedjan.
        if (this.initialSkogsvardsTyp !== this.skogsvardsAtgard.atgardstyp &&
            (!this.skogsvardService.isSiktrojning(this.initialSkogsvardsTyp, this.skogsvardsAtgard.atgardsgrupp) && this.skogsvardsAtgard.isSiktrojning) ||
            (this.skogsvardService.isSiktrojning(this.initialSkogsvardsTyp, this.skogsvardsAtgard.atgardsgrupp) && !this.skogsvardsAtgard.isSiktrojning)) {
            this.atgardKedjaMediatorService.updateAtgardskedjaForAtgard(this.skogsvardsAtgard);

            // Kontrollera om vi ändrat åtgärdstypen från siktröjning
            if (this.initialSkogsvardsTyp === SkogsvardAtgardsTyp.siktrojning) {
                this.skogsvardsAtgard.indirektKostnad = null;
            }
        }
        this.atgardMediatorService.update(this.skogsvardsAtgard);
    }

    private validate(isInit: boolean): boolean {
        let result = true;

        this.clearValidationMessages("skogsvardsatgarddetaljer");
        this.clearValidationMessages("skogsvardsatgard");

        if (!isInit && !this.isLoading && !this.skogsvardsAtgard.isReadonly) {
            result = this.validatePlantleverans() && this.validateSiktrojningProduktionsstatus() && this.validateGeometrier();
        }

        return result;
    }

    private validateGeometrier(): boolean {
        const isApplicable = this.kontraktGeometrier &&
            this.skogsvardsAtgard.status === Planeringsstatus.Tillgangligt &&
            !this.kontrakt.isLeveransvirke &&
            !this.skogsvardsAtgard.isPlantleverans;

        if (!isApplicable) {
            return true;
        }

        const larmpunkter = this.kontraktGeometrier.filter(x => x.isLarmpunkt);
        const larmpunkterIsValid = larmpunkter.length >= 1;
        this.validationService.updateClientSideValidation(!larmpunkterIsValid, this.createValidationMessage({
            message: "Kontrakt saknar larmkoordinat, minst 1 krävs.",
            messageId: "skogsvardsatgardLarmpunkt",
            severity: RuleSeverityLevel.error
        }));

        const kojplatserValid = this.kontraktGeometrier.some(p => p.isKojplats);
        this.validationService.updateClientSideValidation(!kojplatserValid, this.createValidationMessage({
            message: "Kontrakt saknar kojplats. Minst 1 krävs.",
            messageId: "skogsvardsatgardKojplats",
            severity: RuleSeverityLevel.error
        }));

        return larmpunkterIsValid && kojplatserValid;
    }

    private validatePlantleverans(): boolean {
        if (!this.isPlantleverans()) {
            return true;
        }

        if (this.isNullOrEmpty(this.skogsvardsAtgard.anteckningSkogsvard)) {
            const message = this.createValidationMessage({
                entityType: "skogsvardsatgarddetaljer",
                message: "Ange utlämningsplats för plantleverans.",
                messageId: "skogsvardsatgarddetaljeranteckning",
                severity: RuleSeverityLevel.warning
            });

            this.validationService.updateClientSideValidation(true, message);
            return false;
        }

        return true;
    }

    private validateSiktrojningProduktionsstatus(): boolean {
        if (!this.isRojning()) {
            return true;
        }

        const atgardsKedja = this.atgardKedjaMediatorService.getAtgardKedjaKopplingarByAtgard(this.skogsvardsAtgard.guid);
        const aoProduktionsStatus = this.skogsvardService.getArbetsorderProduktionsstatus(this.atgarder, atgardsKedja);

        if (this.initialSkogsvardsTyp !== this.skogsvardsAtgard.atgardstyp &&
            this.skogsvardService.isSiktrojning(this.skogsvardsAtgard.atgardstyp, this.skogsvardsAtgard.atgardsgrupp) &&
            !this.skogsvardService.isValidProduktionsstatusForSiktrojning(aoProduktionsStatus)) {
            const message = this.createValidationMessage({
                entityType: "skogsvardsatgarddetaljer",
                message: `När arbetsordern för den avverkningsåtgärd som skogsvården är kopplad till har produktionsstatus "${aoProduktionsStatus}",
                    går det inte att byta åtgärdstyp till Siktröjning för den åtgärden.`,
                messageId: "skogsvardsatgardSiktrojningPlaneringstatus",
                severity: RuleSeverityLevel.error
            });

            this.validationService.updateClientSideValidation(true, message);
            return false;
        }

        return true;
    }
}
